// SPDX-License-Identifier: AGPL-3.0-or-later

// LOCAL-ONLY: what the agenda, the month grid and the People list show (calendar-plan.md sections 10 and 12): days
// grouped with "Today · …" / "Tomorrow · …", past events under a collapsed "Past", a card's stripe in the event's
// colour or else its creator's, "Added by <name>" / "From <name>'s phone", the bell's lead time when the banner is
// on, the lock on read-only events, and the month grid from week_start. Exclude from upstream sync.

import {
	addDays,
	cardTime,
	dayHeading,
	eventDayKey,
	eventDayKeys,
	eventEndMs,
	eventStartMs,
} from '@app/features/calendar/CalendarFormat';
import {bellLeadText, fill, CalendarStrings as S, WEEKDAY_INITIALS} from '@app/features/calendar/CalendarStrings';
import type {Calendar, CalendarEvent, CalendarMember} from '@app/features/calendar/CalendarTypes';
import {DateTime} from 'luxon';

/** The display name, else the username, else "Someone" (an unknown member, or one with neither, such as a deleted account). */
export function memberName(calendar: Calendar, userId: string | null): string {
	const member = userId == null ? undefined : calendar.members.find((entry) => entry.id === userId);
	if (member == null) return S.someone;
	if (member.global_name != null && member.global_name.trim().length > 0) return member.global_name;
	return member.username.trim().length > 0 ? member.username : S.someone;
}

/** The event's colour, else its creator's (null colour follows the creator); null when neither is known. */
export function eventColour(event: CalendarEvent, calendar: Calendar): string | null {
	if (event.color != null) return event.color;
	return calendar.members.find((member) => member.id === event.created_by)?.color ?? null;
}

export function sourceLine(event: CalendarEvent, calendar: Calendar): string {
	if (event.origin === 'phone') return fill(S.fromPhone, {name: memberName(calendar, event.origin_user_id)});
	return fill(S.addedBy, {name: memberName(calendar, event.created_by)});
}

/** Only your own colour can be changed (section 12, decision 3). */
export function canEditMemberColour(member: CalendarMember, currentUserId: string | null): boolean {
	return currentUserId != null && member.id === currentUserId;
}

export interface AgendaCard {
	readonly event: CalendarEvent;
	readonly time: string;
	readonly title: string;
	readonly source: string;
	readonly colour: string | null;
	/** The banner is on. */
	readonly bell: boolean;
	readonly bellLead: string | null;
	readonly readOnly: boolean;
}

export interface AgendaGroup {
	readonly dayKey: string;
	readonly heading: string;
	readonly cards: ReadonlyArray<AgendaCard>;
}

export interface Agenda {
	readonly past: ReadonlyArray<AgendaGroup>;
	readonly upcoming: ReadonlyArray<AgendaGroup>;
}

export function agendaCard(event: CalendarEvent, calendar: Calendar): AgendaCard {
	return {
		event,
		time: cardTime(event, calendar.timezone),
		title: event.title,
		source: sourceLine(event, calendar),
		colour: eventColour(event, calendar),
		bell: event.banner,
		bellLead: event.banner ? bellLeadText(event.reminder_minutes) : null,
		readOnly: event.read_only,
	};
}

export function sortEvents(events: Iterable<CalendarEvent>, timezone: string): Array<CalendarEvent> {
	return [...events]
		.filter((event) => !event.deleted)
		.sort(
			(a, b) =>
				eventStartMs(a, timezone) - eventStartMs(b, timezone) ||
				Number(b.all_day) - Number(a.all_day) ||
				a.title.localeCompare(b.title) ||
				a.id.localeCompare(b.id),
		);
}

function group(events: ReadonlyArray<CalendarEvent>, calendar: Calendar, todayKey: string): Array<AgendaGroup> {
	const groups: Array<{dayKey: string; heading: string; cards: Array<AgendaCard>}> = [];
	for (const event of events) {
		const dayKey = eventDayKey(event, calendar.timezone);
		let last = groups[groups.length - 1];
		if (last == null || last.dayKey !== dayKey) {
			last = {dayKey, heading: dayHeading(dayKey, todayKey), cards: []};
			groups.push(last);
		}
		last.cards.push(agendaCard(event, calendar));
	}
	return groups;
}

/** An event is past once it has ended; one under way stays upcoming, under its start's day. */
export function buildAgenda(events: Iterable<CalendarEvent>, calendar: Calendar, now: number): Agenda {
	const timezone = calendar.timezone;
	const todayKey = DateTime.fromMillis(now, {zone: timezone}).toISODate() as string;
	const sorted = sortEvents(events, timezone);
	const past = sorted.filter((event) => eventEndMs(event, timezone) <= now);
	const upcoming = sorted.filter((event) => eventEndMs(event, timezone) > now);
	return {past: group(past, calendar, todayKey), upcoming: group(upcoming, calendar, todayKey)};
}

/** Every day with an event, for the mini month's underlines and the month grid. */
export function eventsByDay(events: Iterable<CalendarEvent>, timezone: string): Map<string, Array<CalendarEvent>> {
	const byDay = new Map<string, Array<CalendarEvent>>();
	for (const event of sortEvents(events, timezone)) {
		for (const day of eventDayKeys(event, timezone)) {
			const list = byDay.get(day);
			if (list == null) byDay.set(day, [event]);
			else list.push(event);
		}
	}
	return byDay;
}

/** The weekday initials from the calendar's first weekday (0 = Sunday). */
export function weekdayInitials(weekStart: number): Array<string> {
	return Array.from({length: 7}, (_, index) => WEEKDAY_INITIALS[(weekStart + index) % 7]);
}

/** The month's weeks (YYYY-MM-DD), each starting on week_start, from the week holding the 1st to the one holding the last. */
export function monthWeeks(monthKey: string, weekStart: number): Array<Array<string>> {
	const first = DateTime.fromISO(`${monthKey}-01`, {zone: 'UTC'});
	const offset = ((first.weekday % 7) - weekStart + 7) % 7;
	let day = addDays(first.toISODate() as string, -offset);
	const lastKey = first.endOf('month').toISODate() as string;
	const weeks: Array<Array<string>> = [];
	while (day <= lastKey) {
		const week: Array<string> = [];
		for (let index = 0; index < 7; index++) {
			week.push(day);
			day = addDays(day, 1);
		}
		weeks.push(week);
	}
	return weeks;
}

/** How many chips a month grid day shows before "+<n> more". */
export const MONTH_DAY_CHIP_LIMIT = 3;
